import React, { useState } from 'react';
import {
  endOfMonth, endOfQuarter, endOfWeek, endOfYear, format, startOfMonth, startOfQuarter,
  startOfWeek, startOfYear, subDays, subMonths, subQuarters, subWeeks, subYears,
} from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CalendarDays, ChevronDown, X } from 'lucide-react';

const f = (d) => format(d, 'yyyy-MM-dd');
const wk = { weekStartsOn: 1 };
const fyStart = (d) => new Date(d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1, 3, 1);

export const PRESETS = [
  ['today', 'Today', (n) => [n, n]],
  ['yesterday', 'Yesterday', (n) => [subDays(n, 1), subDays(n, 1)]],
  ['last7', 'Last 7 Days', (n) => [subDays(n, 6), n]],
  ['last30', 'Last 30 Days', (n) => [subDays(n, 29), n]],
  ['last90', 'Last 90 Days', (n) => [subDays(n, 89), n]],
  ['this_week', 'This Week', (n) => [startOfWeek(n, wk), n]],
  ['last_week', 'Last Week', (n) => [startOfWeek(subWeeks(n, 1), wk), endOfWeek(subWeeks(n, 1), wk)]],
  ['this_month', 'This Month', (n) => [startOfMonth(n), n]],
  ['last_month', 'Last Month', (n) => [startOfMonth(subMonths(n, 1)), endOfMonth(subMonths(n, 1))]],
  ['this_quarter', 'This Quarter', (n) => [startOfQuarter(n), n]],
  ['last_quarter', 'Last Quarter', (n) => [startOfQuarter(subQuarters(n, 1)), endOfQuarter(subQuarters(n, 1))]],
  ['this_year', 'This Year', (n) => [startOfYear(n), n]],
  ['last_year', 'Last Year', (n) => [startOfYear(subYears(n, 1)), endOfYear(subYears(n, 1))]],
  ['this_fy', 'This Financial Year', (n) => [fyStart(n), n]],
  ['last_fy', 'Last Financial Year', (n) => [subYears(fyStart(n), 1), subDays(fyStart(n), 1)]],
];

const labelFor = (from, to, preset) => {
  const p = PRESETS.find(([k]) => k === preset);
  if (p) return p[1];
  if (!from && !to) return 'All Time';
  return `${from || '…'} → ${to || '…'}`;
};

export const DateRangeFilter = ({ label, from, to, preset, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const apply = (key, fn) => {
    const [a, b] = fn(new Date());
    onChange({ from: f(a), to: f(b), preset: key });
    setOpen(false);
  };
  const active = from || to;
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={`${testId}-trigger`}
            className={`h-9 px-3 rounded-md border text-sm flex items-center justify-between gap-2 bg-white transition-colors hover:border-slate-400 ${active ? 'border-indigo-400 text-indigo-700' : 'border-slate-200 text-slate-700'}`}>
            <span className="flex items-center gap-2 truncate"><CalendarDays size={14} className="shrink-0" />{labelFor(from, to, preset)}</span>
            {active ? (
              <X size={14} className="shrink-0" data-testid={`${testId}-clear`} onClick={(e) => { e.stopPropagation(); onChange({ from: '', to: '', preset: '' }); }} />
            ) : <ChevronDown size={14} className="shrink-0 text-slate-400" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[22rem] p-3" align="start" data-testid={`${testId}-popover`}>
          <div className="grid grid-cols-2 gap-1">
            <button type="button" onClick={() => { onChange({ from: '', to: '', preset: '' }); setOpen(false); }}
              className={`text-left text-xs px-2 py-1.5 rounded hover:bg-slate-100 ${!active ? 'bg-indigo-50 text-indigo-700 font-semibold' : ''}`}
              data-testid={`${testId}-preset-all`}>All Time</button>
            {PRESETS.map(([key, name, fn]) => (
              <button key={key} type="button" onClick={() => apply(key, fn)}
                className={`text-left text-xs px-2 py-1.5 rounded hover:bg-slate-100 transition-colors ${preset === key ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-slate-700'}`}
                data-testid={`${testId}-preset-${key}`}>{name}</button>
            ))}
          </div>
          <div className="border-t border-slate-100 mt-3 pt-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-2">Custom Range</div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" className="h-8 text-xs" value={from || ''} max={to || undefined}
                onChange={(e) => onChange({ from: e.target.value, to, preset: 'custom' })} data-testid={`${testId}-custom-from`} />
              <Input type="date" className="h-8 text-xs" value={to || ''} min={from || undefined}
                onChange={(e) => onChange({ from, to: e.target.value, preset: 'custom' })} data-testid={`${testId}-custom-to`} />
            </div>
            <Button size="sm" className="w-full mt-2 h-8" onClick={() => setOpen(false)} data-testid={`${testId}-custom-apply`}>Done</Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
